import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [inviteValid, setInviteValid] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Geen uitnodigingstoken gevonden. Vraag een administrator om een uitnodigingslink.");
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${API_URL}/invites/validate/${token}`);
        const data = await response.json();

        if (response.ok && data.valid) {
          setEmail(data.email);
          setInviteValid(true);
        } else {
          setError(data.detail || "Uitnodiging is niet geldig");
        }
      } catch (err) {
        setError("Fout bij valideren van uitnodiging");
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleRegister = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError("Wachtwoorden komen niet overeen");
      return;
    }

    if (password.length < 6) {
      setError("Wachtwoord moet minimaal 6 tekens bevatten");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      // Register with Supabase
      const { error: signupError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signupError) {
        setError(signupError.message);
        setSubmitting(false);
        return;
      }

      // Mark invite as accepted
      const acceptResponse = await fetch(`${API_URL}/invites/accept/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!acceptResponse.ok) {
        console.error("Failed to mark invite as accepted");
      }

      // Redirect to login with success message
      navigate("/login", {
        state: { message: "Account succesvol aangemaakt! Je kunt nu inloggen." },
      });
    } catch (err) {
      setError("Er is een fout opgetreden bij de registratie");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Uitnodiging valideren...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md border border-gray-200">
        <div className="text-center mb-8">
          <img
            src="/bonamuze-logo.png"
            alt="Bonamuze"
            className="mx-auto h-16 w-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-800">Account aanmaken</h1>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {inviteValid ? (
          <form onSubmit={handleRegister}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email is gekoppeld aan je uitnodiging
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Wachtwoord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Minimaal 6 tekens"
                required
                minLength={6}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bevestig wachtwoord
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                minLength={6}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? "Bezig..." : "Account aanmaken"}
            </button>
          </form>
        ) : (
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              {error || "Deze uitnodiging is niet geldig of is verlopen."}
            </p>
            <Link
              to="/login"
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              Terug naar inloggen
            </Link>
          </div>
        )}

        <div className="mt-6 pt-6 border-t text-center">
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-800">
            Al een account? Inloggen
          </Link>
        </div>
      </div>
    </div>
  );
}
